import React, { useState, useEffect } from 'react';
import { useUser } from '../../context/UserContext';
import { 
  Package, Plus, Edit2, Trash2, Search, ArrowLeft, Filter, 
  ChevronLeft, ChevronRight, Eye, TrendingUp, DollarSign, 
  Box, AlertCircle, Grid3x3, List, Star, Clock, 
  Tag, Layers, Shield, Zap, Download, Settings
} from 'lucide-react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import AddProduct from './AddProduct';
import EditProduct from './EditProduct';
import { useSeller } from '../../context/SellerContext';

const Products = () => {
  const { products, loading, deleteProduct, refreshData } = useSeller();
  const [showAddForm, setShowAddForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [selectedSort, setSelectedSort] = useState('newest');
  const [viewMode, setViewMode] = useState('table');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this product?")) {
      await deleteProduct(id);
    }
  };

  const handleEdit = (product) => {
    setSelectedProduct(product);
    setShowEditForm(true);
  };

  const showToast = (message, type) => {
    const toast = document.createElement('div');
    toast.className = `toast-notification toast-${type}`;
    toast.innerHTML = message;
    toast.style.cssText = `
      position: fixed;
      bottom: 20px;
      right: 20px;
      background: ${type === 'success' ? '#10b981' : '#ef4444'};
      color: white;
      padding: 12px 24px;
      border-radius: 12px;
      z-index: 9999;
      font-size: 14px;
      font-weight: 500;
      animation: slideIn 0.3s ease;
      box-shadow: 0 4px 12px rgba(0,0,0,0.15);
    `;
    document.body.appendChild(toast);
    setTimeout(() => toast.remove(), 3000);
  };

  // Sort products
  const getSortedProducts = () => {
    let sorted = [...filteredProducts];
    switch(selectedSort) {
      case 'newest':
        return sorted.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      case 'oldest':
        return sorted.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
      case 'price-high':
        return sorted.sort((a, b) => b.price - a.price);
      case 'price-low':
        return sorted.sort((a, b) => a.price - b.price);
      case 'stock-low':
        return sorted.sort((a, b) => a.stock - b.stock);
      case 'name-asc':
        return sorted.sort((a, b) => a.name.localeCompare(b.name));
      default:
        return sorted;
    }
  };

  // Filter products
  const filteredProducts = products.filter(product => {
    const matchesSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          (product.sku && product.sku.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesCategory = !selectedCategory || product.category === selectedCategory;
    const matchesStatus = !selectedStatus || 
      (selectedStatus === 'Active' ? product.isActive : !product.isActive);
    return matchesSearch && matchesCategory && matchesStatus;
  });

  const sortedProducts = getSortedProducts();

  // Pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentProducts = sortedProducts.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(sortedProducts.length / itemsPerPage);

  // Statistics
  const totalProducts = products.length;
  const totalStock = products.reduce((sum, p) => sum + p.stock, 0);
  const totalValue = products.reduce((sum, p) => sum + (p.price * p.stock), 0);
  const activeProducts = products.filter(p => p.isActive).length;
  const lowStockCount = products.filter(p => p.stock < 15).length;

  const StatCard = ({ icon: Icon, title, value, subtitle, color, bgColor }) => (
    <div className="stat-card-modern">
      <div className="stat-icon" style={{ backgroundColor: bgColor }}>
        <Icon size={22} style={{ color }} />
      </div>
      <div className="stat-content">
        <h3 className="stat-value">{typeof value === 'number' ? value.toLocaleString() : value}</h3>
        <p className="stat-label">{title}</p>
        {subtitle && <span className="stat-subtitle">{subtitle}</span>}
      </div>
    </div>
  );

  if (showAddForm) {
    return (
      <div className="animate-fadeIn">
        <div className="back-header">
          <button onClick={() => setShowAddForm(false)} className="back-btn">
            <ArrowLeft size={20} />
            <span>Back to Products</span>
          </button>
        </div>
        <AddProduct onSuccess={() => {
          setShowAddForm(false);
          refreshData();
        }} />
      </div>
    );
  }

  if (showEditForm && selectedProduct) {
    return (
      <div className="animate-fadeIn">
        <div className="back-header">
          <button onClick={() => {
            setShowEditForm(false);
            setSelectedProduct(null);
          }} className="back-btn">
            <ArrowLeft size={20} />
            <span>Back to Products</span>
          </button>
        </div>
        <EditProduct 
          product={selectedProduct} 
          onSuccess={() => {
            setShowEditForm(false);
            setSelectedProduct(null);
            refreshData();
          }} 
        />
      </div>
    );
  }

  return (
    <div className="products-page">
      {/* Header */}
      <div className="products-header">
        <div className="header-left">
          <h1 className="page-title">My Products</h1>
          <p className="page-subtitle">Manage your plant inventory and track performance</p>
        </div>
        <div className="header-right">
          <button className="btn-export">
            <Download size={16} />
            Export
          </button>
          <button onClick={() => setShowAddForm(true)} className="btn-primary">
            <Plus size={18} />
            Add New Plant
          </button>
        </div>
      </div>

      {/* Statistics Grid */}
      <div className="stats-grid-modern">
        <StatCard icon={Package} title="Total Products" value={totalProducts} color="#10b981" bgColor="#d1fae5" />
        <StatCard icon={Box} title="Total Stock" value={totalStock} color="#3b82f6" bgColor="#dbeafe" />
        <StatCard icon={DollarSign} title="Inventory Value" value={`₹${totalValue.toLocaleString()}`} color="#f59e0b" bgColor="#fed7aa" />
        <StatCard icon={TrendingUp} title="Active Products" value={activeProducts} color="#8b5cf6" bgColor="#e9d5ff" />
        <StatCard icon={AlertCircle} title="Low Stock Alert" value={lowStockCount} subtitle="Items below 15" color="#ef4444" bgColor="#fee2e2" />
      </div>

      {/* Filters Bar */}
      <div className="filters-container">
        <div className="search-wrapper">
          <Search size={18} className="search-icon" />
          <input 
            type="text" 
            className="search-input" 
            placeholder="Search products by name or SKU..." 
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        
        <div className="filter-group">
          <select className="filter-select" value={selectedCategory} onChange={(e) => setSelectedCategory(e.target.value)}>
            <option value="">All Categories</option>
            <option value="Indoor Plants">🏠 Indoor Plants</option>
            <option value="Outdoor Plants">🌳 Outdoor Plants</option>
            <option value="Flowering Plants">🌸 Flowering Plants</option>
            <option value="Succulents">🌵 Succulents</option>
            <option value="Fruit Plants">🍎 Fruit Plants</option>
          </select>
          
          <select className="filter-select" value={selectedStatus} onChange={(e) => setSelectedStatus(e.target.value)}>
            <option value="">All Status</option>
            <option value="Active">Active</option>
            <option value="Inactive">Inactive</option>
          </select>
          
          <select className="filter-select" value={selectedSort} onChange={(e) => setSelectedSort(e.target.value)}>
            <option value="newest">Newest First</option>
            <option value="oldest">Oldest First</option>
            <option value="price-high">Price: High to Low</option>
            <option value="price-low">Price: Low to High</option>
            <option value="stock-low">Stock: Low to High</option>
            <option value="name-asc">Name: A to Z</option>
          </select>
        </div>
        
        <div className="view-toggle">
          <button className={`view-btn ${viewMode === 'table' ? 'active' : ''}`} onClick={() => setViewMode('table')}>
            <List size={16} />
          </button>
          <button className={`view-btn ${viewMode === 'grid' ? 'active' : ''}`} onClick={() => setViewMode('grid')}>
            <Grid3x3 size={16} />
          </button>
        </div>
      </div>

      {/* Products Content */}
      {loading ? (
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading your products...</p>
        </div>
      ) : currentProducts.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">
            <Package size={64} />
          </div>
          <h3>No products found</h3>
          <p>You haven't added any plants to your store yet. Get started by adding your first product.</p>
          <button onClick={() => setShowAddForm(true)} className="btn-primary">
            <Plus size={18} />
            Add Your First Plant
          </button>
        </div>
      ) : viewMode === 'table' ? (
        <>
          <div className="table-container">
            <table className="products-table">
              <thead>
                <tr>
                  <th>Product</th>
                  <th>Category</th>
                  <th>Price</th>
                  <th>Stock</th>
                  <th>Status</th>
                  <th className="text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {currentProducts.map((product) => (
                  <tr key={product._id}>
                    <td>
                      <div className="product-cell">
                        <div className="product-image">
                          <img src={product.image} alt={product.name} onError={(e) => { e.target.src = '/assets/images/thumbs/plant-placeholder.png'; }} />
                        </div>
                        <div className="product-info">
                          <h6 className="product-name">{product.name}</h6>
                          <span className="product-sku">SKU: {product.sku || 'N/A'}</span>
                        </div>
                      </div>
                    </td>
                    <td>
                      <span className="category-badge">{product.category}</span>
                    </td>
                    <td>
                      <div className="price-info">
                        <span className="current-price">₹{product.price}</span>
                        {product.originalPrice > product.price && (
                          <span className="original-price">₹{product.originalPrice}</span>
                        )}
                      </div>
                    </td>
                    <td>
                      <div className={`stock-badge ${product.stock < 15 ? 'low' : product.stock < 30 ? 'medium' : 'high'}`}>
                        {product.stock} left
                      </div>
                    </td>
                    <td>
                      <div className={`status-badge ${product.isActive ? 'active' : 'inactive'}`}>
                        <span className="status-dot"></span>
                        {product.isActive ? 'Active' : 'Inactive'}
                      </div>
                    </td>
                    <td className="text-right">
                      <div className="action-buttons">
                        <button onClick={() => handleEdit(product)} className="action-btn edit" title="Edit Product">
                          <Edit2 size={16} />
                        </button>
                        <button onClick={() => handleDelete(product._id)} className="action-btn delete" title="Delete Product">
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {/* Pagination */}
          {totalPages > 1 && (
            <div className="pagination">
              <button 
                className="page-btn" 
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
              >
                <ChevronLeft size={16} /> Previous
              </button>
              <div className="page-numbers">
                {[...Array(totalPages)].map((_, i) => (
                  <button
                    key={i}
                    className={`page-number ${currentPage === i + 1 ? 'active' : ''}`}
                    onClick={() => setCurrentPage(i + 1)}
                  >
                    {i + 1}
                  </button>
                ))}
              </div>
              <button 
                className="page-btn" 
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
              >
                Next <ChevronRight size={16} />
              </button>
            </div>
          )}
        </>
      ) : (
        <div className="products-grid-view">
          {currentProducts.map((product) => (
            <div key={product._id} className="product-card-grid">
              <div className="card-image">
                <img src={product.image} alt={product.name} onError={(e) => { e.target.src = '/assets/images/thumbs/plant-placeholder.png'; }} />
                <div className="card-badges">
                  {product.stock < 15 && <span className="badge-low">Low Stock</span>}
                  {product.discountPercentage > 0 && <span className="badge-sale">-{product.discountPercentage}%</span>}
                </div>
                <div className="card-actions">
                  <button onClick={() => handleEdit(product)} className="card-action-btn">
                    <Edit2 size={14} />
                  </button>
                  <button onClick={() => handleDelete(product._id)} className="card-action-btn delete">
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
              <div className="card-content">
                <h6 className="card-title">{product.name}</h6>
                <div className="card-category">{product.category}</div>
                <div className="card-price">
                  <span className="current">₹{product.price}</span>
                  {product.originalPrice > product.price && (
                    <span className="original">₹{product.originalPrice}</span>
                  )}
                </div>
                <div className="card-stats">
                  <div className="stat">
                    <Box size={12} />
                    <span>Stock: {product.stock}</span>
                  </div>
                  <div className="stat">
                    <Star size={12} />
                    <span>{product.rating || 4.5}</span>
                  </div>
                </div>
                <div className={`status-indicator ${product.isActive ? 'active' : 'inactive'}`}>
                  {product.isActive ? 'Active' : 'Inactive'}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <style>{`.products-page {
  padding: 24px;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  min-height: 100vh;
}

.products-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 32px;
  flex-wrap: wrap;
  gap: 16px;
}

.header-right {
  display: flex;
  gap: 12px;
}

.btn-export,
.btn-primary {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  border-radius: 40px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
}

.btn-export {
  background: white;
  border: 1px solid #e2e8f0;
  color: #475569;
}

.btn-export:hover {
  border-color: #10b981;
  color: #10b981;
  transform: translateY(-2px);
}

.btn-primary {
  background: linear-gradient(135deg, #2d6a4f 0%, #1a472a 100%);
  border: none;
  color: white;
}

.btn-primary:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(45, 106, 79, 0.3);
}

.stats-grid-modern {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 20px;
  margin-bottom: 32px;
}

.stat-card-modern {
  background: white;
  border-radius: 20px;
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 16px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.04);
  transition: all 0.3s ease;
  border: 1px solid #e8ecef;
}

.stat-card-modern:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 24px rgba(0,0,0,0.1);
}

.stat-icon {
  width: 52px;
  height: 52px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 16px;
}

.stat-value {
  font-size: 26px;
  font-weight: 700;
  color: #1e293b;
  margin: 0;
  line-height: 1.2;
}

.stat-label {
  font-size: 13px;
  color: #64748b;
  margin: 4px 0 0;
}

.stat-subtitle {
  font-size: 11px;
  color: #94a3b8;
}

.filters-container {
  background: white;
  border-radius: 20px;
  padding: 20px;
  margin-bottom: 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.04);
  border: 1px solid #e8ecef;
}

.search-wrapper {
  position: relative;
  flex: 1;
  max-width: 320px;
}

.search-input {
  width: 100%;
  padding: 10px 16px 10px 42px;
  border: 1px solid #e2e8f0;
  border-radius: 40px;
  font-size: 14px;
  transition: all 0.3s ease;
}

.search-input:focus {
  outline: none;
  border-color: #10b981;
  box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.1);
}

.filter-group {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}

.filter-select {
  padding: 8px 16px;
  border: 1px solid #e2e8f0;
  border-radius: 40px;
  font-size: 13px;
  background: white;
  cursor: pointer;
}

.view-toggle {
  display: flex;
  gap: 4px;
  background: #f1f5f9;
  padding: 4px;
  border-radius: 12px;
}

.view-btn {
  padding: 6px 10px;
  border: none;
  background: transparent;
  border-radius: 8px;
  cursor: pointer;
  color: #64748b;
  transition: all 0.3s ease;
}

.view-btn.active {
  background: white;
  color: #10b981;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
}

.table-container {
  background: white;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0,0,0,0.04);
  border: 1px solid #e8ecef;
}

.products-table {
  width: 100%;
  border-collapse: collapse;
}

.products-table th {
  text-align: left;
  padding: 16px 20px;
  background: #f8fafc;
  font-size: 13px;
  font-weight: 600;
  color: #475569;
  border-bottom: 1px solid #e8ecef;
}

.products-table td {
  padding: 16px 20px;
  border-bottom: 1px solid #e8ecef;
  vertical-align: middle;
}

.products-table tr:hover {
  background: #f8fafc;
}

.product-cell {
  display: flex;
  align-items: center;
  gap: 12px;
}

.product-image {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  overflow: hidden;
  background: #f1f5f9;
}

.product-name {
  font-weight: 600;
  color: #1e293b;
  margin: 0 0 4px;
  font-size: 14px;
}

.product-sku {
  font-size: 11px;
  color: #94a3b8;
}

.category-badge {
  padding: 4px 10px;
  background: #f1f5f9;
  border-radius: 20px;
  font-size: 12px;
  color: #475569;
}

.price-info {
  display: flex;
  flex-direction: column;
}

.current-price {
  font-weight: 600;
  color: #10b981;
}

.original-price {
  font-size: 11px;
  color: #94a3b8;
  text-decoration: line-through;
}

.stock-badge {
  display: inline-block;
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 500;
}

.stock-badge.high {
  background: #d1fae5;
  color: #10b981;
}

.stock-badge.medium {
  background: #fed7aa;
  color: #f59e0b;
}

.stock-badge.low {
  background: #fee2e2;
  color: #ef4444;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 500;
}

.status-badge.active {
  background: #d1fae5;
  color: #10b981;
}

.status-badge.inactive {
  background: #f1f5f9;
  color: #64748b;
}

.status-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: currentColor;
}

.action-btn {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #e2e8f0;
  background: white;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.action-btn.edit:hover {
  background: #4f46e5;
  border-color: #4f46e5;
  color: white;
  transform: scale(1.05);
}

.action-btn.delete:hover {
  background: #ef4444;
  border-color: #ef4444;
  color: white;
  transform: scale(1.05);
}

.pagination {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 24px;
  flex-wrap: wrap;
  gap: 16px;
}

.page-btn {
  padding: 8px 16px;
  border: 1px solid #e2e8f0;
  background: white;
  border-radius: 40px;
  font-size: 13px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.3s ease;
}

.page-btn:hover:not(:disabled) {
  border-color: #10b981;
  color: #10b981;
}

.page-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.page-numbers {
  display: flex;
  gap: 6px;
}

.page-number {
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #e2e8f0;
  background: white;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.page-number.active {
  background: #10b981;
  border-color: #10b981;
  color: white;
}

.page-number:hover:not(.active) {
  border-color: #10b981;
  color: #10b981;
}

.products-grid-view {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 20px;
}

.product-card-grid {
  background: white;
  border-radius: 20px;
  overflow: hidden;
  transition: all 0.3s ease;
  border: 1px solid #e8ecef;
}

.product-card-grid:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 24px rgba(0,0,0,0.1);
}

.card-image {
  position: relative;
  height: 200px;
  overflow: hidden;
}

.card-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;
}

.product-card-grid:hover .card-image img {
  transform: scale(1.05);
}

.card-badges {
  position: absolute;
  top: 12px;
  left: 12px;
  display: flex;
  gap: 6px;
}

.badge-low,
.badge-sale {
  padding: 4px 8px;
  border-radius: 20px;
  font-size: 10px;
  font-weight: 600;
}

.badge-low {
  background: #ef4444;
  color: white;
}

.badge-sale {
  background: #f59e0b;
  color: white;
}

.card-actions {
  position: absolute;
  top: 12px;
  right: 12px;
  display: flex;
  gap: 6px;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.product-card-grid:hover .card-actions {
  opacity: 1;
}

.card-action-btn {
  width: 32px;
  height: 32px;
  background: white;
  border: none;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
}

.card-action-btn.edit:hover {
  background: #4f46e5;
  color: white;
}

.card-action-btn.delete:hover {
  background: #ef4444;
  color: white;
}

.card-content {
  padding: 16px;
}

.card-title {
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
  margin: 0 0 6px;
}

.card-category {
  font-size: 11px;
  color: #94a3b8;
  margin-bottom: 10px;
}

.card-price {
  display: flex;
  align-items: baseline;
  gap: 8px;
  margin-bottom: 12px;
}

.card-price .current {
  font-size: 16px;
  font-weight: 700;
  color: #10b981;
}

.card-price .original {
  font-size: 12px;
  color: #94a3b8;
  text-decoration: line-through;
}

.card-stats {
  display: flex;
  gap: 16px;
  margin-bottom: 12px;
}

.card-stats .stat {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  color: #64748b;
}

.status-indicator {
  display: inline-block;
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 500;
}

.status-indicator.active {
  background: #d1fae5;
  color: #10b981;
}

.status-indicator.inactive {
  background: #f1f5f9;
  color: #64748b;
}

.loading-state,
.empty-state {
  text-align: center;
  padding: 60px 20px;
  background: white;
  border-radius: 20px;
}

.empty-icon {
  width: 80px;
  height: 80px;
  margin: 0 auto 20px;
  background: #f1f5f9;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #94a3b8;
}

.back-header {
  margin-bottom: 24px;
}

.back-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: none;
  border: none;
  color: #64748b;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.back-btn:hover {
  color: #10b981;
  transform: translateX(-4px);
}

@media (max-width: 768px) {
  .products-page {
    padding: 16px;
  }

  .stats-grid-modern {
    grid-template-columns: 1fr;
  }

  .filters-container {
    flex-direction: column;
    align-items: stretch;
  }

  .search-wrapper {
    max-width: none;
  }

  .filter-group {
    justify-content: stretch;
  }

  .filter-select {
    flex: 1;
  }

  .products-table {
    font-size: 12px;
  }

  .product-image {
    width: 36px;
    height: 36px;
  }
}`}</style>
    </div>
  );
};

export default Products;